import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useTranslation } from "react-i18next";

const CHART_COLORS = [
    "#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981",
    "#06b6d4", "#f97316", "#6366f1", "#14b8a6", "#e11d48",
];

function getColor(index) {
    return CHART_COLORS[index % CHART_COLORS.length];
}

function formatDate(dateStr) {
    const d = new Date(dateStr + "T00:00:00");
    return `${d.getMonth() + 1}/${d.getDate()}`;
}

function formatValue(val, isUsd) {
    const formatted = val.toFixed(2);
    return isUsd ? "$" + formatted : formatted + " cr";
}

function CustomTooltip({ active, payload, label, isUsd }) {
    if (!active || !payload?.length) return null;
    const items = payload.filter((p) => p.value > 0).sort((a, b) => b.value - a.value);
    const total = items.reduce((sum, p) => sum + p.value, 0);
    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs max-w-[220px]">
            <p className="font-medium text-gray-900 mb-1.5">{label}</p>
            {items.map((item) => (
                <div key={item.dataKey} className="flex items-center justify-between gap-3 py-0.5">
                    <div className="flex items-center gap-1.5 min-w-0">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.fill }} />
                        <span className="text-gray-600 truncate">{item.dataKey}</span>
                    </div>
                    <span className="font-medium text-gray-900 flex-shrink-0">{formatValue(item.value, isUsd)}</span>
                </div>
            ))}
            {items.length > 1 && (
                <div className="flex justify-between pt-1.5 mt-1 border-t border-gray-100 font-medium text-gray-900">
                    <span>Total</span>
                    <span>{formatValue(total, isUsd)}</span>
                </div>
            )}
        </div>
    );
}

export default function CreditUsageChart({ chartData = [], labels = [], platform = "agentui" }) {
    const { t } = useTranslation();
    const isUsd = platform !== "agentui";

    if (!chartData.length) {
        return (
            <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
                {t("billing.usage.noData")}
            </div>
        );
    }

    const displayData = chartData.map((d) => ({ ...d, date: formatDate(d.date) }));

    return (
        <ResponsiveContainer width="100%" height={280}>
            <BarChart data={displayData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip isUsd={isUsd} />} />
                {labels.map((label, i) => (
                    <Bar
                        key={label}
                        dataKey={label}
                        stackId="usage"
                        fill={getColor(i)}
                        radius={i === labels.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                    />
                ))}
            </BarChart>
        </ResponsiveContainer>
    );
}
